import { Q } from '@nozbe/watermelondb'
import { updateAccountOnServer } from 'services/api/account'
import LocalStorage from 'services/LocalStorage'
import Logger from 'services/Logger'
import { AccountDevice, AccountDoc } from 'types/api/changes'
import { CollectionType } from 'types/model'
import { LocalStorageKey } from 'types/services/localStorage'
import { getOrUpdateDeviceId } from 'utils/device'
import { getFCMToken } from 'utils/firebase'
import Database from '../Database'
import { is24meApp, isGroupcalApp } from 'utils/appType'
import { isCypress } from 'utils/cypress'

export const Account = async () => {
  const accounts = await Database.getCollection<AccountDoc>(
    CollectionType.ACCOUNT
  )
    .query()
    .fetch()
  return accounts[0]
}

export async function updatePushTokenIfNeeded() {
  try {
    const token = await getFCMToken()
    Logger.debug('push token', token)
    if (!token) return

    Account().then((acc) => {
      if (acc == null) return
      if (!acc.Devices) acc.Devices = []

      const deviceUUID = getOrUpdateDeviceId()

      function evaluateMoreThanOneDeviceWithPushToken(
        devices: AccountDevice[]
      ) {
        return devices?.filter((e) => e.DevicesID == token).length > 1
      }

      const isMoreThanOneDeviceWithPushToken =
        evaluateMoreThanOneDeviceWithPushToken(acc.Devices)

      function evaluateMoreThanOneDeviceWithUUID(devices: AccountDevice[]) {
        return devices?.filter((e) => e.DeviceUUID == deviceUUID).length > 1
      }

      const isMoreThanOneDeviceWithUUID = evaluateDuplicateDeviceUUID(
        acc.Devices
      )

      function evaluateDuplicateDeviceUUID(devices: AccountDevice[]) {
        const deviceUUIDCounts = new Map<string, number>()
        devices.forEach((item: { DeviceUUID: string }) => {
          deviceUUIDCounts.set(
            item.DeviceUUID,
            (deviceUUIDCounts.get(item.DeviceUUID) || 0) + 1
          )
        })

        return Array.from(deviceUUIDCounts.values()).some((count) => count > 1)
      }

      const deviceUUIDCounts = new Map<string, number>()
      acc.Devices.forEach((item: { DeviceUUID: string }) => {
        deviceUUIDCounts.set(
          item.DeviceUUID,
          (deviceUUIDCounts.get(item.DeviceUUID) || 0) + 1
        )
      })

      const hasDuplicateDeviceUUID = evaluateDuplicateDeviceUUID(acc.Devices)
      Logger.debug(
        'Account:Device:hasDuplicateDeviceUUID',
        hasDuplicateDeviceUUID
      )
      const isDeviceWithTokenInArray =
        acc.Devices?.find(
          (e) => e.DevicesID == token && e.DeviceUUID == deviceUUID
        ) !== undefined

      const isTokenWithoutDevice =
        acc.Devices?.find(
          (e) => e.DevicesID == token && e.DeviceUUID !== deviceUUID
        ) !== undefined

      const isDeviceWithoutToken =
        acc.Devices?.find(
          (e) => e.DevicesID !== token && e.DeviceUUID == deviceUUID
        ) !== undefined

      if (
        isDeviceWithTokenInArray &&
        !isMoreThanOneDeviceWithPushToken &&
        !isMoreThanOneDeviceWithUUID &&
        !hasDuplicateDeviceUUID
      ) {
        Logger.debug('Account:Device:token already exists')
        return
      }

      Logger.debug(
        'Account:Device:ismoreThanOneDeviceWithPushToken',
        isMoreThanOneDeviceWithPushToken
      )
      Logger.debug('Account:Device:token without device', isTokenWithoutDevice)
      Logger.debug('Account:Device:device without token', isDeviceWithoutToken)

      let newDevices = acc.Devices

      if (
        !isCypress() &&
        !isDeviceWithTokenInArray &&
        !isTokenWithoutDevice &&
        !isDeviceWithoutToken
      ) {
        Logger.debug('Account:Device: device not found; adding it')
        // we does not have such device, adding such
        newDevices = [...acc.Devices]
        newDevices.push({
          AppID: is24meApp() ? 'twentyfourme' : 'groupcal',
          DeviceType: '3',
          DeviceUUID: deviceUUID,
          DevicesID: token,
          Token: LocalStorage.get(LocalStorageKey.ACCESS_TOKEN) ?? ''
        })
      }

      if (isTokenWithoutDevice) {
        newDevices = acc.Devices.map((device) => {
          return device.DevicesID === token
            ? {
                ...device,
                DeviceUUID: deviceUUID,
                AppID: is24meApp() ? 'twentyfourme' : 'groupcal'
              }
            : device
        })
      }

      if (isDeviceWithoutToken) {
        // we have such device, but with different token, updating it
        newDevices = acc.Devices.map((device) => {
          return device.DeviceUUID === deviceUUID
            ? {
                ...device,
                DevicesID: token,
                AppID: is24meApp() ? 'twentyfourme' : 'groupcal'
              }
            : device
        })
      }

      if (evaluateMoreThanOneDeviceWithPushToken(newDevices)) {
        // keep all devices with other tokens, but only one with current push token
        newDevices = [...newDevices.filter((e) => e.DevicesID !== token)]

        const last = acc.Devices.findLast(
          (e) => e.DeviceUUID === deviceUUID && e.DevicesID === token
        )
        if (last) {
          newDevices.push({
            ...last,
            AppID: is24meApp() ? 'twentyfourme' : 'groupcal'
          })
        }
        Logger.debug(
          'Account:Device: more than one device with push token, keeping only current'
        )
      }

      if (
        evaluateDuplicateDeviceUUID(newDevices) ||
        evaluateMoreThanOneDeviceWithUUID(newDevices)
      ) {
        const map = new Map()
        acc.Devices.forEach((item) => {
          map.set(item.DeviceUUID, item) // overwrites previous with same DeviceUUID
        })

        const deduped = Array.from(map.values())
        newDevices = deduped
      }

      Database.write(async () => {
        const accFromDB = (await Database.getById(
          CollectionType.ACCOUNT,
          acc.id
        )) as unknown as AccountDoc
        await accFromDB.update(() => {
          accFromDB.Devices = newDevices
        })

        const response = await updateAccountOnServer(accFromDB)

        Logger.debug('add push', response)
      })
    })
  } catch (e) {
    console.error('no way to receive a push token', e)
  }
}
